import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Iconify from "../iconify/Iconify";
import { beltGrades } from "../../sections/@dashboard/members/defaultStatisticValues";
import {
  useCreateTournamentMutation,
  useUpdateTournamentMutation,
} from "../../features/tournaments/tournamentsApiSlice";

const defaultDisciplines = [
  "Hyong",
  "Bruchtest",
  "Ilbo-Taeryon",
  "Freikampf",
  "Waffen",
  "Selbstverteidigung",
];

const buildPlacementDefaults = () => [
  { place: 1, firstName: "", lastName: "", location: "" },
  { place: 2, firstName: "", lastName: "", location: "" },
  { place: 3, firstName: "", lastName: "", location: "" },
];

const normalizeTournament = (t) => {
  if (!t) {
    return {
      tournamentName: "",
      tournamentDate: "",
      locationName: "",
      description: "",
      disciplines: [],
    };
  }

  const tournamentDate = t.tournamentDate
    ? new Date(t.tournamentDate).toISOString().slice(0, 10)
    : "";

  const disciplines = Array.isArray(t.disciplines) ? t.disciplines : [];

  return {
    tournamentName: t.tournamentName || "",
    tournamentDate,
    locationName: t.locationName || "",
    description: t.description || "",
    disciplines: disciplines.map((d) => ({
      name: d?.name || "",
      beltGrades: Array.isArray(d?.beltGrades)
        ? d.beltGrades.map((g) => ({
            grade: g?.grade || "",
            participantCount: Number.isFinite(Number(g?.participantCount))
              ? Number(g.participantCount)
              : 0,
            placements:
              Array.isArray(g?.placements) && g.placements.length
                ? g.placements.map((p, idx) => ({
                    place: p?.place ?? idx + 1,
                    firstName: p?.firstName || "",
                    lastName: p?.lastName || "",
                    location: p?.location || "",
                  }))
                : buildPlacementDefaults(),
          }))
        : [],
    })),
  };
};

export default function TournamentFormDialog({ initialTournament, onClose, onSaved }) {
  const [form, setForm] = useState(() => normalizeTournament(initialTournament));
  const [customDiscipline, setCustomDiscipline] = useState("");
  const [selectDiscipline, setSelectDiscipline] = useState("");
  const [activeDiscipline, setActiveDiscipline] = useState(0);
  const [expandedGrades, setExpandedGrades] = useState({});

  const [createTournament, { isLoading: isCreating }] =
    useCreateTournamentMutation();
  const [updateTournament, { isLoading: isUpdating }] =
    useUpdateTournamentMutation();

  useEffect(() => {
    setForm(normalizeTournament(initialTournament));
    setActiveDiscipline(0);
    setExpandedGrades({});
  }, [initialTournament]);

  const availableDefaultDisciplines = useMemo(() => {
    const existing = new Set(form.disciplines.map((d) => d.name.toLowerCase()));
    return defaultDisciplines.filter(
      (d) => !existing.has(d.toLowerCase())
    );
  }, [form.disciplines]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const addDiscipline = (name) => {
    const clean = String(name || "").trim();
    if (!clean) return;
    const exists = form.disciplines.some(
      (d) => d.name.toLowerCase() === clean.toLowerCase()
    );
    if (exists) return;
    setForm((prev) => ({
      ...prev,
      disciplines: [...prev.disciplines, { name: clean, beltGrades: [] }],
    }));
    setActiveDiscipline(form.disciplines.length);
  };

  const handleAddDefault = () => {
    if (!selectDiscipline) return;
    addDiscipline(selectDiscipline);
    setSelectDiscipline("");
  };

  const handleAddCustom = () => {
    addDiscipline(customDiscipline);
    setCustomDiscipline("");
  };

  const removeDiscipline = (idx) => {
    setForm((prev) => ({
      ...prev,
      disciplines: prev.disciplines.filter((_, i) => i !== idx),
    }));
    setActiveDiscipline((prev) => {
      if (prev > idx) return prev - 1;
      if (prev === idx) return 0;
      return prev;
    });
  };

  const updateDisciplineName = (idx, value) => {
    setForm((prev) => ({
      ...prev,
      disciplines: prev.disciplines.map((d, i) =>
        i === idx ? { ...d, name: value } : d
      ),
    }));
  };

  const updateDisciplineGrades = useCallback((idx, selectedGrades) => {
    const normalized = Array.isArray(selectedGrades)
      ? selectedGrades
      : String(selectedGrades || "").split(",").filter(Boolean);
    setForm((prev) => {
      const disciplines = [...prev.disciplines];
      const discipline = disciplines[idx];
      if (!discipline) return prev;

      const byGrade = new Map(
        discipline.beltGrades.map((g) => [g.grade, g])
      );
      const nextGrades = normalized.map((grade) => {
        const existing = byGrade.get(grade);
        return (
          existing || {
            grade,
            participantCount: 0,
            placements: buildPlacementDefaults(),
          }
        );
      });

      disciplines[idx] = { ...discipline, beltGrades: nextGrades };
      return { ...prev, disciplines };
    });
  }, []);

  const updateParticipantCount = useCallback((dIdx, gIdx, value) => {
    const count = Number(value);
    setForm((prev) => {
      const disciplines = [...prev.disciplines];
      const discipline = disciplines[dIdx];
      if (!discipline) return prev;
      const beltGrades = [...discipline.beltGrades];
      const grade = beltGrades[gIdx];
      if (!grade) return prev;
      beltGrades[gIdx] = {
        ...grade,
        participantCount: Number.isFinite(count) ? count : 0,
      };
      disciplines[dIdx] = { ...discipline, beltGrades };
      return { ...prev, disciplines };
    });
  }, []);

  const updatePlacementField = useCallback((dIdx, gIdx, pIdx, field, value) => {
    setForm((prev) => {
      const disciplines = [...prev.disciplines];
      const discipline = disciplines[dIdx];
      if (!discipline) return prev;
      const beltGrades = [...discipline.beltGrades];
      const grade = beltGrades[gIdx];
      if (!grade) return prev;
      const placements = Array.isArray(grade.placements)
        ? [...grade.placements]
        : buildPlacementDefaults();
      const placement = placements[pIdx] || {
        place: pIdx + 1,
        firstName: "",
        lastName: "",
        location: "",
      };
      placements[pIdx] = { ...placement, [field]: value };
      beltGrades[gIdx] = { ...grade, placements };
      disciplines[dIdx] = { ...discipline, beltGrades };
      return { ...prev, disciplines };
    });
  }, []);

  const handleSubmit = async () => {
    const payload = {
      tournamentName: form.tournamentName.trim(),
      tournamentDate: form.tournamentDate || null,
      locationName: form.locationName.trim(),
      description: form.description.trim(),
      disciplines: form.disciplines,
    };

    try {
      if (initialTournament?._id || initialTournament?.id) {
        await updateTournament({
          id: initialTournament._id || initialTournament.id,
          ...payload,
        }).unwrap();
      } else {
        await createTournament(payload).unwrap();
      }
      onSaved?.();
    } catch (err) {
      console.error("Tournament save failed", err);
    }
  };

  const activeDisciplineData = form.disciplines[activeDiscipline];

  const handleToggleGrade = (dIdx, grade) => {
    setExpandedGrades((prev) => {
      const key = `${dIdx}-${grade}`;
      return { ...prev, [key]: !prev[key] };
    });
  };

  const setAllGradesExpanded = (dIdx, grades, expand) => {
    setExpandedGrades((prev) => {
      const next = { ...prev };
      grades.forEach((g) => {
        const key = `${dIdx}-${g.grade}`;
        next[key] = expand;
      });
      return next;
    });
  };

  const handleSelectAllGrades = (dIdx) => {
    updateDisciplineGrades(dIdx, [...beltGrades]);
    setAllGradesExpanded(
      dIdx,
      beltGrades.map((grade) => ({ grade })),
      false
    );
  };

  const handleClearGrades = (dIdx) => {
    updateDisciplineGrades(dIdx, []);
    setExpandedGrades((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${dIdx}-`)) delete next[key];
      });
      return next;
    });
  };

  const renderGradeValue = (selected) => {
    if (!selected?.length) return "Keine Auswahl";
    if (selected.length <= 4) return selected.join(", ");
    return `${selected.length} Gürtelgrade`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Typography variant="h6">
          {initialTournament ? "Turnier bearbeiten" : "Neues Turnier"}
        </Typography>

        <Stack spacing={2}>
          <TextField
            label="Turniername"
            value={form.tournamentName}
            onChange={handleChange("tournamentName")}
            fullWidth
            required
          />
          <TextField
            label="Datum"
            type="date"
            value={form.tournamentDate}
            onChange={handleChange("tournamentDate")}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Ort"
            value={form.locationName}
            onChange={handleChange("locationName")}
            fullWidth
          />
          <TextField
            label="Beschreibung"
            value={form.description}
            onChange={handleChange("description")}
            fullWidth
            multiline
            rows={3}
          />
        </Stack>

        <Divider />

        <Stack spacing={2}>
          <Typography variant="subtitle1">Disziplinen</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="discipline-select-label">
                Disziplin aus Liste
              </InputLabel>
              <Select
                labelId="discipline-select-label"
                label="Disziplin aus Liste"
                value={selectDiscipline}
                onChange={(e) => setSelectDiscipline(e.target.value)}
              >
                {availableDefaultDisciplines.map((discipline) => (
                  <MenuItem key={discipline} value={discipline}>
                    {discipline}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              onClick={handleAddDefault}
              sx={{ minWidth: 160 }}
              startIcon={<Iconify icon="mdi:plus" />}
            >
              Hinzufügen
            </Button>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Eigene Disziplin"
              value={customDiscipline}
              onChange={(e) => setCustomDiscipline(e.target.value)}
              fullWidth
            />
            <Button
              variant="outlined"
              onClick={handleAddCustom}
              sx={{ minWidth: 160 }}
              startIcon={<Iconify icon="mdi:plus" />}
            >
              Hinzufügen
            </Button>
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="stretch"
          >
            <Box
              sx={{
                minWidth: { md: 220 },
                border: "1px solid rgba(0,0,0,0.1)",
                borderRadius: 2,
                p: 1,
                maxHeight: { md: 520 },
                overflowY: "auto",
              }}
            >
              {form.disciplines.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Füge eine Disziplin hinzu, um loszulegen.
                  </Typography>
                </Box>
              )}
              {form.disciplines.map((discipline, idx) => {
                const isActive = idx === activeDiscipline;
                return (
                  <Button
                    key={`${discipline.name}-${idx}`}
                    onClick={() => setActiveDiscipline(idx)}
                    variant={isActive ? "contained" : "text"}
                    color={isActive ? "secondary" : "inherit"}
                    fullWidth
                    sx={{
                      justifyContent: "space-between",
                      mb: 0.5,
                      textTransform: "none",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ textAlign: "left", flexGrow: 1 }}
                    >
                      {discipline.name || "Unbenannt"}
                    </Typography>
                    <Chip size="small" label={discipline.beltGrades.length} />
                  </Button>
                );
              })}
            </Box>

            <Box sx={{ flexGrow: 1 }}>
              {!form.disciplines.length && (
                <Box
                  sx={{
                    p: 3,
                    border: "1px dashed rgba(0,0,0,0.2)",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Füge eine Disziplin hinzu, um Gürtelgrade und Ergebnisse zu erfassen.
                  </Typography>
                </Box>
              )}

              {activeDisciplineData && (
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid rgba(0,0,0,0.1)",
                    borderRadius: 2,
                  }}
                >
                  <Stack spacing={2}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems={{ sm: "center" }}
                    >
                      <TextField
                        label="Disziplin"
                        value={activeDisciplineData.name}
                        onChange={(e) =>
                          updateDisciplineName(activeDiscipline, e.target.value)
                        }
                        fullWidth
                      />
                      <Button
                        color="error"
                        onClick={() => removeDiscipline(activeDiscipline)}
                        startIcon={<Iconify icon="mdi:trash-outline" />}
                      >
                        Entfernen
                      </Button>
                    </Stack>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleSelectAllGrades(activeDiscipline)}
                      >
                        Alle Gürtelgrade auswählen
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleClearGrades(activeDiscipline)}
                      >
                        Auswahl löschen
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() =>
                          setAllGradesExpanded(
                            activeDiscipline,
                            activeDisciplineData.beltGrades,
                            true
                          )
                        }
                      >
                        Alle aufklappen
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() =>
                          setAllGradesExpanded(
                            activeDiscipline,
                            activeDisciplineData.beltGrades,
                            false
                          )
                        }
                      >
                        Alle zuklappen
                      </Button>
                    </Stack>

                    <FormControl fullWidth>
                      <InputLabel id={`belt-grades-${activeDiscipline}`}>
                        Gürtelgrade auswählen
                      </InputLabel>
                      <Select
                        labelId={`belt-grades-${activeDiscipline}`}
                        multiple
                        value={activeDisciplineData.beltGrades.map((g) => g.grade)}
                        onChange={(e) =>
                          updateDisciplineGrades(activeDiscipline, e.target.value)
                        }
                        input={<OutlinedInput label="Gürtelgrade auswählen" />}
                        renderValue={renderGradeValue}
                      >
                        {beltGrades.map((grade) => (
                          <MenuItem key={grade} value={grade}>
                            {grade}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {activeDisciplineData.beltGrades.length === 0 && (
                      <Box
                        sx={{
                          p: 2,
                          border: "1px dashed rgba(0,0,0,0.2)",
                          borderRadius: 2,
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Wähle Gürtelgrade aus, um Teilnehmerzahlen und Platzierungen
                          zu erfassen.
                        </Typography>
                      </Box>
                    )}

                    {activeDisciplineData.beltGrades.map((grade, gIdx) => (
                      <GradeAccordion
                        key={`${grade.grade}-${gIdx}`}
                        grade={grade}
                        gradeIndex={gIdx}
                        disciplineIndex={activeDiscipline}
                        expanded={
                          expandedGrades[`${activeDiscipline}-${grade.grade}`] ||
                          false
                        }
                        onToggle={handleToggleGrade}
                        onParticipantChange={updateParticipantCount}
                        onPlacementChange={updatePlacementField}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          </Stack>
        </Stack>

        <Divider />

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={onClose} variant="outlined">
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="secondary"
            disabled={!form.tournamentName.trim() || isCreating || isUpdating}
          >
            Speichern
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

const GradeAccordion = React.memo(function GradeAccordion({
  grade,
  gradeIndex,
  disciplineIndex,
  expanded,
  onToggle,
  onParticipantChange,
  onPlacementChange,
}) {
  const placementsFilled = useMemo(
    () =>
      grade.placements?.filter(
        (p) => p.firstName || p.lastName || p.location
      ).length,
    [grade.placements]
  );

  const handleParticipantChange = useCallback(
    (event) =>
      onParticipantChange(disciplineIndex, gradeIndex, event.target.value),
    [disciplineIndex, gradeIndex, onParticipantChange]
  );

  const handlePlacementChange = useCallback(
    (pIdx, field) => (event) =>
      onPlacementChange(
        disciplineIndex,
        gradeIndex,
        pIdx,
        field,
        event.target.value
      ),
    [disciplineIndex, gradeIndex, onPlacementChange]
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={() => onToggle(disciplineIndex, grade.grade)}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary expandIcon={<Iconify icon="mdi:chevron-down" />}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ sm: "center" }}
          sx={{ width: "100%", justifyContent: "space-between" }}
        >
          <Typography variant="subtitle2">{grade.grade}</Typography>
          <Stack direction="row" spacing={1}>
            <Chip
              size="small"
              label={`Teilnehmer: ${grade.participantCount || 0}`}
            />
            <Chip
              size="small"
              label={`Plätze ausgefüllt: ${placementsFilled || 0}/3`}
            />
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {expanded && (
          <Stack spacing={2}>
            <TextField
              label="Teilnehmerzahl"
              type="number"
              value={grade.participantCount}
              onChange={handleParticipantChange}
              inputProps={{ min: 0 }}
              sx={{ maxWidth: 200 }}
            />
            <Stack spacing={1}>
              {grade.placements.map((placement, pIdx) => (
                <Stack
                  key={`${grade.grade}-${placement.place}`}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                >
                  <TextField
                    label={`${placement.place}. Platz Vorname`}
                    value={placement.firstName}
                    onChange={handlePlacementChange(pIdx, "firstName")}
                    fullWidth
                  />
                  <TextField
                    label={`${placement.place}. Platz Nachname`}
                    value={placement.lastName}
                    onChange={handlePlacementChange(pIdx, "lastName")}
                    fullWidth
                  />
                  <TextField
                    label={`${placement.place}. Platz Standort`}
                    value={placement.location}
                    onChange={handlePlacementChange(pIdx, "location")}
                    fullWidth
                  />
                </Stack>
              ))}
            </Stack>
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
});
